import { call, put, takeEvery } from "redux-saga/effects"
import { isOnline } from "../../offline/isOnline"
import { isOfflineFeatureEnabled } from "../../offline/featureFlag"
import { enqueue } from "../../offline/queue"
import { newIdempotencyKey } from "../../offline/idempotency"
import { refreshOfflineCounters } from "./routesSaga"
import { updateToken } from "../../utils/updateToken"
import { visitReportsAPI } from "../api/api"
import { generalActions } from "../store/reducers/generalReducer"
import { actions, actionTypes } from "../store/reducers/visitsReducers"
import { SagaWrapper } from './index';
import i18n from "../../res/languages";

function* getAllVisits({ payload }: any) {
  try {
    yield put(actions.toggleIsLoading(true))
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getAllVisits({ ...payload })
    if (response.status === 200) {
      updateToken()
      yield put(actions.getAllVisitsAC(response.data))
      yield put(actions.toggleIsLoading(false))
    }
  } catch (error) {
    console.log(error)
    yield put(actions.toggleIsLoading(false))
  }
}

function* getVisits(payload: any) {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getVisits(payload.id, payload.page, payload.perPage, payload.scheduled)
    if (response.status === 200) {
      updateToken()
      if (!payload.scheduled) {
        yield put(actions.getVisitsAC(response.data))
      } else {
        yield put(actions.getScheduledVisitsAC(response.data))
      }
    }
  } catch (error) {
    console.log(error)
  }
}

function* getStatuses() {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getStatuses()
    if (response.status === 200) {
      updateToken()
      yield put(actions.getStatusesAC(response.data))
    }
  } catch (error) {
    console.log(error)
  }
}

function* getReactions() {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getReactions()
    if (response.status === 200) {
      updateToken()
      yield put(actions.getReactionsAC(response.data))
    }
  } catch (error) {
    console.log(error)
  }
}

function* getAddressError() {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getAddressError()
    if (response.status === 200) {
      updateToken()
      yield put(actions.getAddressErrorAC(response.data))
    }
  } catch (error) {
    console.log(error)
  }
}

export function* getNextVisit(payload: any) {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.getNextHouseholdVisit(payload.id)
    if (response.status === 200) {
      updateToken()
      yield put(actions.getNextVisitAC(response.data.data))
    }
  } catch (error) {
    // Say so rather than only logging. The report form refuses to start from
    // another household's visit, so a swallowed failure here left an empty form
    // and nothing on screen to explain it.
    yield put(actions.nextVisitUnavailableAC())
  }
}

function* createNextVisit(payload: any) {
  try {
    const response: { status: number; data: { data: {} } } =
      yield visitReportsAPI.createNextVisit(payload.id, payload.data)

    if (response.status === 200) {
      updateToken()
      yield put(
        generalActions.successAC({ message: i18n.t("contact_info.next_visit_scheduled"), type: "success" })
      )
      yield getVisits({ id: payload.userId, scheduled: 1 })
      yield getVisits({ id: payload.userId, scheduled: 0 })
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || i18n.t("general.an_error_occurred"),
        type: "error",
      })
    )
  }
}

export function* creationNextVisit({ request, action, dispatchResponse }: any): any {
  const { id, data: body } = action.payload;

  if (!isOnline() && isOfflineFeatureEnabled()) {
    // The report is the most valuable thing a volunteer produces in the field, so it
    // is never allowed to surface a network error. Persist it and confirm; the sync
    // engine replays it with this same Idempotency-Key, which is what stops a later
    // retry from creating a second visit.
    yield call(enqueue, {
      type: "SUBMIT_VISIT_REPORT",
      entityKey: `visit:${id}`,
      payload: { id, data: body },
      idempotencyKey: newIdempotencyKey(),
    })
    yield call(refreshOfflineCounters)

    const onSuccess = action.callbacks?.onSuccess ?? action.payload?.callbacks?.onSuccess
    if (typeof onSuccess === "function") {
      yield call(onSuccess, null)
    }
    return
  }

  const { data, error } = yield request({
    method: "put",
    endpoint: `/api/auth/visits/${action.payload.id}`,
    body: action.payload.data
  })

  yield dispatchResponse(
    { data, error },
    actionTypes.SUCCESS_CREATION_NEXT_VISIT,
    actionTypes.ERROR_CREATION_NEXT_VISIT
  )
}

function* updateVisit({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: 'put',
    endpoint: '/api/auth/visits/' + action.payload.id,
    body: action.payload.data,
  });

  yield dispatchResponse(
    { data, error },
    actionTypes.UPDATE_VISIT_SUCCESS,
    actionTypes.UPDATE_VISIT_ERROR,
  );
}

function* deleteVisit({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: 'delete',
    endpoint: '/api/auth/visits/' + action.data.id
  });

  yield dispatchResponse(
    { data, error },
    actionTypes.DELETE_VISIT_SUCCESS,
    actionTypes.DELETE_VISIT_ERROR,
  );
}

function* exportVisits({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: 'get',
    endpoint: '/api/auth/visits/export',
    params: action.payload,
    responseType: 'arraybuffer',
  });

  yield dispatchResponse(
    { data, error },
    actionTypes.EXPORT_VISITS_SUCCESS,
    actionTypes.EXPORT_VISITS_ERROR,
  );
}

export function* visitsWatcher() {
  yield takeEvery(actionTypes.GET_ALL_VISITS_TRIGGER, getAllVisits)
  yield takeEvery(actionTypes.GET_VISITS_TRIGGER, getVisits)
  yield takeEvery(actionTypes.GET_STATUSES_TRIGGER, getStatuses)
  yield takeEvery(actionTypes.GET_REACTIONS_TRIGGER, getReactions)
  yield takeEvery(actionTypes.GET_ADDRESS_ERROR_TRIGGER, getAddressError)
  yield takeEvery(actionTypes.GET_NEXT_VISIT_TRIGGER, getNextVisit)
  yield takeEvery(actionTypes.CREATE_NEXT_VISIT_TRIGGER, createNextVisit)
  yield takeEvery(actionTypes.CREATION_NEXT_VISIT, SagaWrapper.withRequest(creationNextVisit))
  yield takeEvery(actionTypes.UPDATE_VISIT_TRIGGER, SagaWrapper.withRequest(updateVisit))
  yield takeEvery(actionTypes.DELETE_VISIT_TRIGGER, SagaWrapper.withRequest(deleteVisit))
  yield takeEvery(actionTypes.EXPORT_VISITS_TRIGGER, SagaWrapper.withRequest(exportVisits))
}
